import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiService from '../services/apiService';

// All money values are stored canonically in INR on the server. This store's
// `currency` + `fxRate` only control how amounts are displayed and entered.
// fxRate is defined as: 1 unit of `currency` = fxRate INR.
// So for currency=USD with 1 USD = 83 INR, fxRate = 83.
// formatCurrency(inrAmount) => inrAmount / fxRate, rendered with display symbol.

const useSettingsStore = create(
  persist(
    (set, get) => ({
      currency: 'INR',
      currencySymbol: '₹',
      currencyLocale: 'en-IN',
      fxRate: 1,
      timezone: 'UTC',
      dateFormat: 'MM/DD/YYYY',

      currencyConfig: {
        USD: { symbol: '$', locale: 'en-US', name: 'US Dollar' },
        EUR: { symbol: '€', locale: 'de-DE', name: 'Euro' },
        GBP: { symbol: '£', locale: 'en-GB', name: 'British Pound' },
        INR: { symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
        JPY: { symbol: '¥', locale: 'ja-JP', name: 'Japanese Yen' },
        CAD: { symbol: 'C$', locale: 'en-CA', name: 'Canadian Dollar' },
        AUD: { symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' }
      },

      timezoneConfig: {
        'UTC': { name: 'UTC', offset: '+00:00' },
        'America/New_York': { name: 'Eastern Time', offset: '-05:00' },
        'America/Los_Angeles': { name: 'Pacific Time', offset: '-08:00' },
        'Europe/London': { name: 'London', offset: '+00:00' },
        'Asia/Kolkata': { name: 'India', offset: '+05:30' }
      },

      setCurrency: (newCurrency) => {
        const config = get().currencyConfig[newCurrency];
        if (config) {
          set({
            currency: newCurrency,
            currencySymbol: config.symbol,
            currencyLocale: config.locale
          });
        }
      },

      setFxRate: (newRate) => {
        const rate = Number(newRate);
        if (!isFinite(rate) || rate <= 0) return;
        set({ fxRate: rate });
      },

      setTimezone: (newTimezone) => {
        set({ timezone: newTimezone });
      },

      setDateFormat: (newDateFormat) => {
        set({ dateFormat: newDateFormat });
      },

      // Convert an INR-base amount into the current display currency.
      convertFromBase: (inrAmount) => {
        const { fxRate } = get();
        if (inrAmount === null || inrAmount === undefined || isNaN(inrAmount)) return 0;
        const rate = fxRate && fxRate > 0 ? fxRate : 1;
        return inrAmount / rate;
      },

      // Convert an amount entered in the display currency to INR for persistence.
      convertToBase: (displayAmount) => {
        const { fxRate } = get();
        if (displayAmount === null || displayAmount === undefined || isNaN(displayAmount)) return 0;
        const rate = fxRate && fxRate > 0 ? fxRate : 1;
        return displayAmount * rate;
      },

      // Format an INR-base amount in the display currency.
      formatCurrency: (amount, options = {}) => {
        const { currency, currencyLocale, fxRate, currencySymbol } = get();
        const {
          minimumFractionDigits = 0,
          maximumFractionDigits = 2,
          showSymbol = true
        } = options;

        if (amount === null || amount === undefined || isNaN(amount)) {
          return showSymbol ? `${currencySymbol}0` : '0';
        }

        const rate = fxRate && fxRate > 0 ? fxRate : 1;
        const displayAmount = amount / rate;

        try {
          const formatter = new Intl.NumberFormat(currencyLocale, {
            style: showSymbol ? 'currency' : 'decimal',
            currency: currency,
            minimumFractionDigits,
            maximumFractionDigits
          });
          return formatter.format(displayAmount);
        } catch (error) {
          const symbol = showSymbol ? currencySymbol : '';
          return `${symbol}${displayAmount.toLocaleString()}`;
        }
      },

      formatDate: (date, options = {}) => {
        const { dateFormat, timezone } = get();
        const { includeTime = false } = options;

        if (!date) return '';

        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';

        try {
          let formatOptions = {};

          switch (dateFormat) {
            case 'MM/DD/YYYY':
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
              break;
            case 'DD/MM/YYYY':
              formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
              break;
            case 'YYYY-MM-DD':
              formatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
              break;
            default:
              formatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
          }

          if (includeTime) {
            formatOptions.hour = '2-digit';
            formatOptions.minute = '2-digit';
            formatOptions.timeZone = timezone;
          }

          return dateObj.toLocaleDateString('en-US', formatOptions);
        } catch (error) {
          return dateObj.toLocaleDateString();
        }
      },

      formatTime: (date) => {
        const { timezone } = get();

        if (!date) return '';

        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return '';

        try {
          return dateObj.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone
          });
        } catch (error) {
          return dateObj.toLocaleTimeString();
        }
      },

      fetchSettings: async () => {
        try {
          const response = await apiService.get('/admin/tenant/settings');
          const settings = response.data.data;

          if (settings.preferences) {
            const { currency, fxRate, timezone, dateFormat } = settings.preferences;

            if (currency) get().setCurrency(currency);
            if (fxRate !== undefined && fxRate !== null) get().setFxRate(fxRate);
            if (timezone) get().setTimezone(timezone);
            if (dateFormat) get().setDateFormat(dateFormat);
          }

          return { success: true };
        } catch (error) {
          console.error('Failed to fetch settings:', error);
          return { success: false, error: error.message };
        }
      },

      updateSettings: async (newSettings) => {
        try {
          const currentSettings = await apiService.get('/admin/tenant/settings');
          const existingSettings = currentSettings.data.data;

          const updatedSettings = {
            ...existingSettings,
            preferences: {
              ...existingSettings.preferences,
              ...newSettings
            }
          };

          await apiService.put('/admin/tenant/settings', updatedSettings);

          if (newSettings.currency) get().setCurrency(newSettings.currency);
          if (newSettings.fxRate !== undefined) get().setFxRate(newSettings.fxRate);
          if (newSettings.timezone) get().setTimezone(newSettings.timezone);
          if (newSettings.dateFormat) get().setDateFormat(newSettings.dateFormat);

          return { success: true };
        } catch (error) {
          console.error('Failed to update settings:', error);
          return { success: false, error: error.message };
        }
      }
    }),
    {
      name: 'system-settings',
      partialize: (state) => ({
        currency: state.currency,
        currencySymbol: state.currencySymbol,
        currencyLocale: state.currencyLocale,
        fxRate: state.fxRate,
        timezone: state.timezone,
        dateFormat: state.dateFormat
      })
    }
  )
);

export default useSettingsStore;
